import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const SIZES = ["S", "M", "L", "XL", "XXL"];

export default function ProductDetailSheet({ product, open, onOpenChange }) {
  const [selectedSize, setSelectedSize] = useState(null);
  const { addItem, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    if (!open) setSelectedSize(null);
  }, [open]);

  if (!product) return null;

  const handleAdd = () => {
    if (!selectedSize) {
      toast("Pick a size first");
      return;
    }
    addItem(product, selectedSize);
    toast.success(`Added — ${product.name} (${selectedSize})`);
    onOpenChange(false);
    setTimeout(() => setCartOpen(true), 250);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-white text-black border-l border-black/10 w-full sm:max-w-lg rounded-none p-0 flex flex-col"
        data-testid="product-detail-sheet"
      >
        <SheetHeader className="px-8 pt-10 pb-2">
          <SheetTitle
            className="text-3xl md:text-4xl font-black tracking-tight uppercase"
            data-testid="detail-product-name"
          >
            {product.name}
          </SheetTitle>
          <SheetDescription className="text-[11px] tracking-[0.25em] uppercase text-black/50">
            Size · Add to Bag
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div className="w-full aspect-square flex items-center justify-center">
            <img
              src={product.image}
              alt={product.name}
              className={`w-full max-w-[420px] aspect-square object-contain select-none ${
                product.id === "crewneck" ? "scale-125 mix-blend-multiply" : ""
              } ${product.id === "hoodie" ? "scale-125" : ""}`}
              draggable={false}
              data-testid="detail-product-image"
            />
          </div>

          <div className="mt-8">
            <p className="text-lg font-bold tracking-tight" data-testid="detail-price">
              ${product.price.toFixed(2)}
            </p>
            <p className="text-[11px] tracking-[0.25em] uppercase text-black/50 mt-1">
              {product.description}
            </p>
          </div>

          <div className="mt-8">
            <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-black/60 mb-3">
              Select Size
            </p>
            <div className="flex gap-2 flex-wrap" role="radiogroup" aria-label="Size">
              {SIZES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="meao-size"
                  data-selected={selectedSize === s}
                  onClick={() => setSelectedSize(s)}
                  data-testid={`detail-size-btn-${product.id}-${s}`}
                  aria-checked={selectedSize === s}
                  role="radio"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-black/10 px-8 py-6">
          <button
            type="button"
            onClick={handleAdd}
            className="meao-btn w-full"
            data-testid={`detail-add-to-cart-${product.id}`}
          >
            Add to Cart
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
